import { useSnackbar } from 'notistack';
import React from 'react';
import { useHistory } from 'react-router-dom';
import { useCreateThirdPartyChargePoint } from '../../../../../app/ApiGen';
import { useCurrentOrganisation } from '../../../../../app/CurrentOrganisationProvider';
import { errorSnackbar } from '../../../../../components/ErrorSnackbar';
import { Card, Section, Wizard, Workspace } from '../../../../../design-system';
import { usePostChargePointEvnex } from '../../../../../utils/api';
import {
  mapWizardStateToRequestCreateEvnexChargePoint,
  mapWizardStateToRequestCreateThirdPartyChargePoint,
} from '../../../../../utils/chargePoints/mappers';
import { ConfirmationStep } from './components/ConfirmationStep';
import { DetailsStep } from './components/DetailsStep';
import { ElectricityPriceStep } from './components/ElectricityPriceStep';
import { LocationStep } from './components/LocationStep';
import { VendorStep } from './components/VendorStep';
import { CreateChargePointWizardState, CreateThirdPartyChargePointWizardState } from './types';

type OnComplete = (state: CreateChargePointWizardState & CreateThirdPartyChargePointWizardState) => void;

function usePostEvnexChargePoint(args: { organisationId: string }) {
  const { organisationId } = args;
  const { mutate } = usePostChargePointEvnex({});

  return React.useCallback(
    async (state: CreateChargePointWizardState) => {
      await mutate(mapWizardStateToRequestCreateEvnexChargePoint({ organisationId, state }));
    },
    [mutate, organisationId],
  );
}

function usePostThirdPartyChargePoint(args: { organisationId: string }) {
  const { organisationId } = args;
  const { mutate } = useCreateThirdPartyChargePoint({ orgId: organisationId });

  return React.useCallback(
    async (state: CreateThirdPartyChargePointWizardState) => {
      await mutate(mapWizardStateToRequestCreateThirdPartyChargePoint(state));
    },
    [mutate],
  );
}

const CreateChargePointView: React.FC = () => {
  const { push } = useHistory();

  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const { organisation } = useCurrentOrganisation();
  const { slug, id: organisationId } = organisation ?? { slug: '', id: '' };

  const postEvnexChargePoint = usePostEvnexChargePoint({ organisationId });
  const postThirdPartyChargePoint = usePostThirdPartyChargePoint({ organisationId });

  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const goToChargePointsList = React.useCallback(() => push(`/organisations/${slug}/charge-points`), [push, slug]);

  const onComplete = React.useCallback<OnComplete>(
    async (state) => {
      setIsSubmitting(true);
      const postChargePoint = state.vendor === 'evnex' ? postEvnexChargePoint : postThirdPartyChargePoint;
      postChargePoint(state)
        .then(() => {
          enqueueSnackbar('Charge point created', { variant: 'success' });
          goToChargePointsList();
        })
        .catch((e) => {
          setIsSubmitting(false);
          errorSnackbar('Unable to add charge point', closeSnackbar, enqueueSnackbar, e);
        });
      return () => {
        setIsSubmitting(false);
      };
    },
    [postEvnexChargePoint, postThirdPartyChargePoint, enqueueSnackbar, goToChargePointsList, closeSnackbar],
  );

  return (
    <Workspace>
      <Section
        title="Create charge point"
        description="Create a new charge point to your organisation. Once completed you will be able to control and view insights for it."
      >
        <Card>
          <Wizard<CreateChargePointWizardState & CreateThirdPartyChargePointWizardState>
            steps={[
              { id: 'vendor', label: 'Vendor', view: VendorStep },
              { id: 'location', label: 'Location', view: LocationStep },
              { id: 'details', label: 'Details', view: DetailsStep },
              { id: 'electricity-price', label: 'Electricity price', view: ElectricityPriceStep },
              { id: 'confirmation', label: 'Confirmation', view: ConfirmationStep },
            ]}
            onComplete={onComplete}
            onExit={goToChargePointsList}
            isLoading={isSubmitting}
            hasStepper
          />
        </Card>
      </Section>
    </Workspace>
  );
};

export default CreateChargePointView;
